import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { MapPin, ExternalLink, Navigation, Loader2 } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default marker icon in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const ProjectMap = ({ project }) => {
  const { t } = useTranslation();
  const [coordinates, setCoordinates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Build the full address string
  const getFullAddress = () => {
    const parts = [
      project?.site_address,
      project?.site_city,
      project?.site_state,
      project?.site_postal_code,
      project?.site_country
    ].filter(Boolean);
    return parts.join(', ');
  };

  const fullAddress = getFullAddress();

  // Geocode the address using Nominatim (free OpenStreetMap geocoding)
  useEffect(() => {
    const geocodeAddress = async () => {
      if (!fullAddress) {
        setError('No address available');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const encodedAddress = encodeURIComponent(fullAddress);
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1`,
          {
            headers: {
              'User-Agent': 'ProjectManagementApp/1.0'
            }
          }
        );

        const data = await response.json();

        if (data && data.length > 0) {
          setCoordinates({
            lat: parseFloat(data[0].lat),
            lng: parseFloat(data[0].lon),
            displayName: data[0].display_name
          });
        } else {
          setError('Address not found on map');
        }
      } catch (err) {
        console.error('Geocoding error:', err);
        setError('Failed to locate address');
      } finally {
        setLoading(false);
      }
    };

    geocodeAddress();
  }, [fullAddress]);

  // Generate Google Maps URL
  const getGoogleMapsUrl = () => {
    if (coordinates) {
      return `https://www.google.com/maps/search/?api=1&query=${coordinates.lat},${coordinates.lng}`;
    }
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
  };

  // Generate directions URL
  const getDirectionsUrl = () => {
    if (coordinates) {
      return `https://www.google.com/maps/dir/?api=1&destination=${coordinates.lat},${coordinates.lng}`;
    }
    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
  };

  if (!fullAddress) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {t('auto.misc2.projectLocation')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-sm">{t('auto.misc2.noSiteAddressConfiguredForThis')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          {t('auto.misc2.projectLocation')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Address Display */}
        <div className="bg-gray-50 p-3 rounded-md">
          <p className="text-sm font-medium text-gray-700">{fullAddress}</p>
        </div>

        {/* Map Container */}
        <div className="relative rounded-lg overflow-hidden border" style={{ height: '300px' }}>
          {loading && (
            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
              <div className="flex items-center gap-2 text-gray-600">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{t('auto.misc2.loadingMap')}</span>
              </div>
            </div>
          )}

          {error && !coordinates && (
            <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-10">
              <div className="text-center">
                <MapPin className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm">{error}</p>
                <p className="text-gray-400 text-xs mt-1">{t('auto.misc2.useTheButtonsBelowToOpen')}</p>
              </div>
            </div>
          )}

          {coordinates && (
            <MapContainer
              center={[coordinates.lat, coordinates.lng]}
              zoom={15}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker position={[coordinates.lat, coordinates.lng]}>
                <Popup>
                  <div className="text-sm">
                    <strong>{project?.name}</strong>
                    <br />
                    {fullAddress}
                  </div>
                </Popup>
              </Marker>
            </MapContainer>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(getGoogleMapsUrl(), '_blank')}
            data-testid="open-google-maps-btn"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {t('auto.misc2.openInGoogleMaps')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(getDirectionsUrl(), '_blank')}
            data-testid="get-directions-btn"
          >
            <Navigation className="h-4 w-4 mr-2" />
            {t('auto.misc2.getDirections')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ProjectMap;
